import { type Accessor, createEffect, onCleanup, onMount } from "solid-js";
import type { PowerSyncDrizzleDB, Todo } from "@listto/powersync";
import type { VirtualRow } from "../types";
import { updateTodoMutation } from "@listto/shared";
import {
  clearDrawerNavigators,
  registerDrawerNavigators,
  triggerFocusSlide,
} from "./use-drawer-swipe-nav";

import type { useLocation, useNavigate } from "@tanstack/solid-router";
import type { useSearchPalette } from "@/features/search/hooks/use-search-palette";
import { useInteraction } from "@/lib/interaction/solid";

interface UseTodoListEventsOptions {
  rowData: Accessor<VirtualRow[]>;
  focusedIndex: Accessor<number | null>;
  setFocusedIndex: (updater: number | null | ((prev: number | null) => number | null)) => void;
  setIsKeyboardFocus: (val: boolean) => void;
  todoInput: Accessor<HTMLInputElement | null>;
  todoToDelete: Accessor<Todo | null>;
  searchPalette: ReturnType<typeof useSearchPalette>;
  setEntranceDirection: (dir: "next" | "prev") => void;
  lastFocusedTodoInput: Accessor<string | null>;
  setLastFocusedTodoInput: (val: string | null) => void;
  completedCollapsed: Accessor<boolean>;
  setCompletedCollapsed: (val: boolean) => void;
  scrollContainerRef: Accessor<HTMLDivElement | null>;
  isSelectionMode: Accessor<boolean>;
  toggleItemSelection: (id: string) => void;
  handleSelectTodo: (id: string, options?: { replace?: boolean }) => void;
  handleToggleTodo: (todo: Todo) => Promise<void> | void;
  handleDeleteTodo: (id: string) => void;
  drizzle: Accessor<PowerSyncDrizzleDB | null>;
  location: ReturnType<typeof useLocation>;
  navigate: ReturnType<typeof useNavigate>;
  EXIT_DURATION_MS: number;
  isCustomList?: Accessor<boolean>;
}

const isInputElement = (el: Element | null): el is HTMLElement =>
  el instanceof HTMLElement && (["INPUT", "TEXTAREA"].includes(el.tagName) || el.isContentEditable);

const getHtmlElement = (selector: string): HTMLElement | null => {
  const el = document.querySelector(selector);
  return el instanceof HTMLElement ? el : null;
};

export function useTodoListEvents(options: UseTodoListEventsOptions) {
  const interaction = useInteraction();
  let lastNonNullFocusedIndex: number | null = null;

  createEffect(() => {
    const idx = options.focusedIndex();
    if (idx !== null) {
      lastNonNullFocusedIndex = idx;
    }
  });

  onMount(() => {
    const handleNavigationKeysWithDrawerOpen = (
      e: KeyboardEvent,
      total: number,
      isFocusInDrawer: boolean,
      isInput: boolean,
    ): boolean => {
      // Vertical keys navigate options inside the details drawer
      if (e.key === "ArrowDown" || e.key === "ArrowUp" || e.key === "j" || e.key === "k") {
        if (!isFocusInDrawer) {
          // Transfer focus to the first interactive element inside the drawer
          const drawer = document.querySelector("#todo-details-drawer");
          if (drawer) {
            const activeDetail = drawer.querySelector('[data-active-todo-detail="true"]');
            const targetContainer = activeDetail ?? drawer;
            const firstFocusable = targetContainer.querySelector(
              'button, input:not([type="hidden"]), textarea, select, [tabindex="0"]',
            );
            if (firstFocusable instanceof HTMLElement) {
              e.preventDefault();
              firstFocusable.focus();
              return true;
            }
          }
        }
        return false;
      }

      // Side arrows (ArrowLeft/ArrowRight/h/l) navigate tasks
      if (e.key === "ArrowRight" || (!isInput && e.key === "l")) {
        e.preventDefault();
        options.setIsKeyboardFocus(true);
        options.setFocusedIndex((prev) => {
          const next = prev === null ? 0 : Math.min(prev + 1, total - 1);
          const row = options.rowData()[next];
          if (row && (row.kind === "pending" || row.kind === "completed")) {
            options.setEntranceDirection("next");
            triggerFocusSlide("next");
            options.handleSelectTodo(row.todo.id, { replace: true });
          }
          return next;
        });
        return true;
      }

      if (e.key === "ArrowLeft" || (!isInput && e.key === "h")) {
        e.preventDefault();
        options.setIsKeyboardFocus(true);
        options.setFocusedIndex((prev) => {
          const next = prev === null ? total - 1 : Math.max(prev - 1, 0);
          const row = options.rowData()[next];
          if (row && (row.kind === "pending" || row.kind === "completed")) {
            options.setEntranceDirection("prev");
            triggerFocusSlide("prev");
            options.handleSelectTodo(row.todo.id, { replace: true });
          }
          return next;
        });
        return true;
      }

      return false;
    };

    const handleNavigationKeysWithDrawerClosed = (
      e: KeyboardEvent,
      total: number,
      isFocusInDrawer: boolean,
      isInput: boolean,
    ): boolean => {
      if (isFocusInDrawer) {
        return false;
      }

      if (e.key === "ArrowDown" || (!isInput && e.key === "j")) {
        e.preventDefault();
        options.setIsKeyboardFocus(true);
        options.setFocusedIndex((prev) => {
          const next = prev === null ? 0 : Math.min(prev + 1, total - 1);
          if (options.location().pathname.includes("/todo/")) {
            const row = options.rowData()[next];
            if (row && (row.kind === "pending" || row.kind === "completed")) {
              options.handleSelectTodo(row.todo.id, { replace: true });
            }
          }
          return next;
        });
        return true;
      }

      if (e.key === "ArrowUp" || (!isInput && e.key === "k")) {
        e.preventDefault();
        options.setIsKeyboardFocus(true);
        const current = options.focusedIndex();
        if (current === 0 && !isInput) {
          const isCustomList = options.isCustomList?.();
          if (isCustomList) {
            options.setFocusedIndex(null);
            const settingsBtn = getHtmlElement("#list-settings-btn");
            const selectBtn = getHtmlElement("#list-select-btn");
            const titleBtn = getHtmlElement("#list-title-edit-btn");
            const iconBtn = getHtmlElement("#list-icon-btn");
            if (settingsBtn) {
              settingsBtn.focus();
            } else if (selectBtn) {
              selectBtn.focus();
            } else if (titleBtn) {
              titleBtn.focus();
            } else if (iconBtn) {
              iconBtn.focus();
            }
          }
          // If dashboard list, stay on task index 0 (do nothing).
        } else {
          options.setFocusedIndex((prev) => {
            const next = prev === null ? 0 : Math.max(prev - 1, 0);
            if (options.location().pathname.includes("/todo/")) {
              const row = options.rowData()[next];
              if (row && (row.kind === "pending" || row.kind === "completed")) {
                options.handleSelectTodo(row.todo.id, { replace: true });
              }
            }
            return next;
          });
        }
        return true;
      }

      return false;
    };

    const handleNavigationKeys = (e: KeyboardEvent, total: number): boolean => {
      const activeEl = document.activeElement;
      const isDrawerOpen = options.location().pathname.includes("/todo/");
      const isFocusInDrawer =
        activeEl instanceof HTMLElement && activeEl.closest("#todo-details-drawer") !== null;
      const isInput =
        activeEl instanceof HTMLElement &&
        (["INPUT", "TEXTAREA"].includes(activeEl.tagName) || activeEl.isContentEditable);

      if (isDrawerOpen) {
        return handleNavigationKeysWithDrawerOpen(e, total, isFocusInDrawer, isInput);
      }
      return handleNavigationKeysWithDrawerClosed(e, total, isFocusInDrawer, isInput);
    };

    const handleActionKeys = (e: KeyboardEvent, activeTodo: Todo, total: number) => {
      const activeEl = document.activeElement;
      const isFocusInDrawer =
        activeEl instanceof HTMLElement && activeEl.closest("#todo-details-drawer") !== null;
      if (isFocusInDrawer) {
        return false;
      }
      if (e.key === "Enter") {
        e.preventDefault();
        if (options.isSelectionMode()) {
          options.toggleItemSelection(activeTodo.id);
        } else {
          options.handleSelectTodo(activeTodo.id);
        }
        return true;
      }
      if (e.key === " " || e.key === "e") {
        e.preventDefault();
        void options.handleToggleTodo(activeTodo);
        return true;
      }
      if (e.key === "i" || e.key === "s") {
        e.preventDefault();
        const dbInstance = options.drizzle();
        if (dbInstance) {
          void updateTodoMutation(dbInstance, activeTodo.id, {
            important: activeTodo.important === 1 ? 0 : 1,
          });
        }
        return true;
      }
      if (e.key === "Delete" || e.key === "Backspace") {
        e.preventDefault();
        options.handleDeleteTodo(activeTodo.id);
        options.setFocusedIndex((prev) => {
          if (prev === null) {
            return null;
          }
          const newTotal = total - 1;
          if (newTotal <= 0) {
            return null;
          }
          return Math.min(prev, newTotal - 1);
        });
        return true;
      }
      return false;
    };

    // Suppression now reads the shared interaction context (dialog/palette
    // flags) instead of querying the DOM on every keystroke. `todoToDelete`
    // stays as a belt-and-suspenders signal for the inline delete confirm.
    const shouldIgnoreHotkeys = (): boolean =>
      options.todoToDelete() !== null ||
      interaction.get("palette.open") ||
      interaction.get("dialog.open");

    const updateLastFocusedInput = (
      activeEl: HTMLElement,
      key: string,
    ): "ignored" | "navigate" | "continue" => {
      const isNoteInput = activeEl.classList.contains("todo-notes-input");
      const isSubtaskItemInput =
        activeEl.tagName === "TEXTAREA" &&
        Boolean(activeEl.closest(".group")) &&
        !activeEl.classList.contains("todo-title-input");

      if (isNoteInput || isSubtaskItemInput) {
        return "ignored";
      }

      if (isInputElement(activeEl)) {
        if (key === "ArrowDown" || key === "ArrowUp") {
          if (activeEl.classList.contains("todo-title-input")) {
            options.setLastFocusedTodoInput("title");
          } else if (activeEl.classList.contains("todo-subtask-add-input")) {
            options.setLastFocusedTodoInput("subtask");
          } else {
            options.setLastFocusedTodoInput("row");
          }
          return "navigate";
        }
        return "ignored";
      }

      if (key === "ArrowDown" || key === "ArrowUp" || key === "j" || key === "k") {
        options.setLastFocusedTodoInput("row");
      }
      return "continue";
    };

    const handleCompletedHeaderKeys = (e: KeyboardEvent): boolean => {
      if (e.key === "Enter" || e.key === " " || e.key === "ArrowLeft" || e.key === "ArrowRight") {
        e.preventDefault();
        if (e.key === "ArrowLeft") {
          options.setCompletedCollapsed(true);
        } else if (e.key === "ArrowRight") {
          options.setCompletedCollapsed(false);
        } else {
          options.setCompletedCollapsed(!options.completedCollapsed());
        }
        return true;
      }
      return false;
    };

    const handleEscapeOnTodoInput = (e: KeyboardEvent, activeEl: Element | null): boolean => {
      if (e.key !== "Escape" || activeEl !== options.todoInput()) {
        return false;
      }
      e.preventDefault();
      const targetIdx = lastNonNullFocusedIndex ?? 0;
      const rows = options.rowData();
      if (rows.length > 0) {
        const clampedIdx = Math.min(targetIdx, rows.length - 1);
        options.setIsKeyboardFocus(true);
        options.setLastFocusedTodoInput("row");
        options.todoInput()?.blur();

        if (options.focusedIndex() === clampedIdx) {
          requestAnimationFrame(() => {
            const row = options.rowData()[clampedIdx];
            if (!row) {
              return;
            }
            if (row.kind === "completed-header") {
              const el = document.querySelector(`#completed-header-${clampedIdx}`);
              if (el instanceof HTMLElement) {
                el.focus();
              }
            } else {
              const el = document.querySelector(`#todo-item-${row.todo.id}`);
              if (el) {
                const focusable = el.querySelector('[tabindex="0"]');
                if (focusable instanceof HTMLElement) {
                  focusable.focus();
                }
              }
            }
            options.setLastFocusedTodoInput(null);
          });
        } else {
          options.setFocusedIndex(clampedIdx);
        }
      }
      return true;
    };

    const handleCtrlArrowScroll = (e: KeyboardEvent): boolean => {
      if (!e.ctrlKey || (e.key !== "ArrowUp" && e.key !== "ArrowDown")) {
        return false;
      }
      e.preventDefault();
      e.stopPropagation();
      const container = options.scrollContainerRef();
      if (container) {
        const scrollStep = 80;
        const scrollAmount = e.key === "ArrowDown" ? scrollStep : -scrollStep;
        container.scrollBy({ top: scrollAmount, behavior: "auto" });
      }
      return true;
    };

    const handleEscapeOnList = (e: KeyboardEvent): boolean => {
      if (e.key !== "Escape") {
        return false;
      }
      if (options.focusedIndex() !== null && !options.location().pathname.includes("/todo/")) {
        e.preventDefault();
        options.setFocusedIndex(null);
      }
      return true;
    };

    const handleRowActionsAndHotkeys = (
      e: KeyboardEvent,
      activeEl: Element | null,
      total: number,
    ) => {
      const idx = options.focusedIndex();
      if (idx === null) {
        return;
      }
      const row = options.rowData()[idx];
      if (!row) {
        return;
      }
      if (row.kind === "completed-header") {
        handleCompletedHeaderKeys(e);
      } else {
        const activeTodo = row.todo;
        const isFocusInDrawer =
          activeEl instanceof HTMLElement && activeEl.closest("#todo-details-drawer") !== null;
        if (!isFocusInDrawer && !isInputElement(activeEl)) {
          handleActionKeys(e, activeTodo, total);
        }
      }
    };

    const handleKeyDown = (e: KeyboardEvent) => {
      if (shouldIgnoreHotkeys()) {
        return;
      }

      const activeEl = document.activeElement;

      if (handleEscapeOnTodoInput(e, activeEl)) {
        return;
      }

      if (handleCtrlArrowScroll(e)) {
        return;
      }

      if (activeEl instanceof HTMLElement) {
        // Header-control navigation is owned by TodoListMainHeader's roving group.
        const action = updateLastFocusedInput(activeEl, e.key);
        if (action === "ignored") {
          return;
        }
      }

      const total = options.rowData().length;
      if (total === 0) {
        return;
      }

      if (handleNavigationKeys(e, total)) {
        return;
      }

      if (handleEscapeOnList(e)) {
        return;
      }

      handleRowActionsAndHotkeys(e, activeEl, total);
    };

    const handleMousedown = () => {
      options.setIsKeyboardFocus(false);
    };

    const navigateDrawerToNext = () => {
      const total = options.rowData().length;
      if (total === 0) {
        return;
      }
      options.setIsKeyboardFocus(true);
      options.setFocusedIndex((prev) => {
        const next = prev === null ? 0 : Math.min(prev + 1, total - 1);
        const row = options.rowData()[next];
        if (row && (row.kind === "pending" || row.kind === "completed")) {
          options.setEntranceDirection("next");
          triggerFocusSlide("next");
          options.handleSelectTodo(row.todo.id, { replace: true });
        }
        return next;
      });
    };

    const navigateDrawerToPrev = () => {
      const total = options.rowData().length;
      if (total === 0) {
        return;
      }
      options.setIsKeyboardFocus(true);
      options.setFocusedIndex((prev) => {
        const next = prev === null ? total - 1 : Math.max(prev - 1, 0);
        const row = options.rowData()[next];
        if (row && (row.kind === "pending" || row.kind === "completed")) {
          options.setEntranceDirection("prev");
          triggerFocusSlide("prev");
          options.handleSelectTodo(row.todo.id, { replace: true });
        }
        return next;
      });
    };

    const hasNextDrawerTodo = () => {
      const rows = options.rowData();
      const current = options.focusedIndex();
      if (rows.length === 0) {
        return false;
      }
      const start = current ?? -1;
      for (let i = start + 1; i < rows.length; i += 1) {
        const row = rows[i];
        if (row && (row.kind === "pending" || row.kind === "completed")) {
          return true;
        }
      }
      return false;
    };

    const hasPrevDrawerTodo = () => {
      const rows = options.rowData();
      const current = options.focusedIndex();
      if (rows.length === 0) {
        return false;
      }
      const start = current ?? rows.length;
      for (let i = start - 1; i >= 0; i -= 1) {
        const row = rows[i];
        if (row && (row.kind === "pending" || row.kind === "completed")) {
          return true;
        }
      }
      return false;
    };

    const getNextDrawerTodoId = (currentTodoId?: string | null): string | null => {
      const rows = options.rowData();
      if (rows.length === 0) {
        return null;
      }
      const start = currentTodoId
        ? rows.findIndex(
            (row) =>
              (row.kind === "pending" || row.kind === "completed") && row.todo.id === currentTodoId,
          )
        : (options.focusedIndex() ?? -1);

      for (let i = start + 1; i < rows.length; i += 1) {
        const row = rows[i];
        if (row && (row.kind === "pending" || row.kind === "completed")) {
          return row.todo.id;
        }
      }
      return null;
    };

    const getPrevDrawerTodoId = (currentTodoId?: string | null): string | null => {
      const rows = options.rowData();
      if (rows.length === 0) {
        return null;
      }
      const start = currentTodoId
        ? rows.findIndex(
            (row) =>
              (row.kind === "pending" || row.kind === "completed") && row.todo.id === currentTodoId,
          )
        : (options.focusedIndex() ?? rows.length);

      if (start === -1) {
        return null;
      }
      for (let i = start - 1; i >= 0; i -= 1) {
        const row = rows[i];
        if (row && (row.kind === "pending" || row.kind === "completed")) {
          return row.todo.id;
        }
      }
      return null;
    };

    registerDrawerNavigators({
      next: navigateDrawerToNext,
      prev: navigateDrawerToPrev,
      hasNext: hasNextDrawerTodo,
      hasPrev: hasPrevDrawerTodo,
      getNextTodoId: getNextDrawerTodoId,
      getPrevTodoId: getPrevDrawerTodoId,
    });
    onCleanup(() => {
      clearDrawerNavigators();
    });

    globalThis.addEventListener("keydown", handleKeyDown);
    globalThis.addEventListener("mousedown", handleMousedown);

    onCleanup(() => {
      globalThis.removeEventListener("keydown", handleKeyDown);
      globalThis.removeEventListener("mousedown", handleMousedown);
    });
  });
}
